import React from 'react'
import { Flex, Link, Text } from '@workwhile/ui'
import { format, parseISO } from 'date-fns'
import { CompumaticPunch } from 'api/admin/timeClock/listCompumaticPunches'
import * as ROUTES from 'constants/routes'
import { ShiftStatusRender } from 'pages/UnpaidShifts/components/UnpaidShiftsRenderers/ShiftStatusRender'
import {
  formatCompumaticPunchStatus,
  formatCompumaticPunchType,
} from '../formatCompumaticLabels'

type CompumaticPunchCellsProps = {
  punch: CompumaticPunch
  compact?: boolean
}

export const CompumaticCompanyCell = ({ punch }: CompumaticPunchCellsProps) => {
  if (punch.company === null) {
    return <Text color="lightText">—</Text>
  }
  return (
    <Text>
      {punch.company.name} (
      <Link to={`/companies/${punch.company.id}`}>{punch.company.id}</Link>)
    </Text>
  )
}

export const CompumaticLocationCell = ({
  punch,
}: CompumaticPunchCellsProps) => {
  if (punch.location === null) {
    return <Text color="lightText">—</Text>
  }
  return (
    <Text>
      {punch.location.name} ({punch.location.id})
    </Text>
  )
}

export const CompumaticCompanyLocationCell = ({
  punch,
}: CompumaticPunchCellsProps) => {
  if (punch.company === null && punch.location === null) {
    return <Text color="lightText">—</Text>
  }

  return (
    <Flex flexDirection="column" gap={0}>
      {punch.company !== null ? (
        <Text>
          {punch.company.name} (
          <Link to={`/companies/${punch.company.id}`}>{punch.company.id}</Link>)
        </Text>
      ) : (
        <Text color="lightText">—</Text>
      )}
      {punch.location !== null ? (
        <Text color="lightText">
          {punch.location.name} ({punch.location.id})
        </Text>
      ) : null}
    </Flex>
  )
}

export const CompumaticWorkerCell = ({ punch }: CompumaticPunchCellsProps) => {
  const label =
    punch.worker.displayName ??
    (punch.worker.userId !== null
      ? `Worker ${punch.worker.userId}`
      : punch.worker.rawWorkerId)

  if (punch.worker.userId !== null) {
    return (
      <Text>
        {label} (
        <Link to={`/user/${punch.worker.userId}`}>{punch.worker.userId}</Link>)
      </Text>
    )
  }

  return <Text>{label}</Text>
}

export const CompumaticPunchCell = ({
  punch,
  compact = false,
}: CompumaticPunchCellsProps) => {
  const punchAtLabel = format(parseISO(punch.punchAt), 'MMM d, yyyy h:mm a')
  const timezoneSuffix =
    punch.locationTimezone !== null ? ` ${punch.locationTimezone}` : ''

  return (
    <Flex flexDirection="column" gap={compact ? 0 : 1}>
      <Text fontSize={compact ? 0 : undefined}>
        {punchAtLabel}
        {timezoneSuffix}
      </Text>
      <Flex alignItems="baseline" gap={1} flexWrap="wrap">
        <Text as="span" fontSize={0} fontWeight={600}>
          {formatCompumaticPunchType(punch.punchType)}
        </Text>
        <Text as="span" fontSize={0} color="lightText">
          {punch.vendorClockId}
        </Text>
      </Flex>
    </Flex>
  )
}

export const CompumaticStatusCell = ({ punch }: CompumaticPunchCellsProps) => (
  <Text>{formatCompumaticPunchStatus(punch.status)}</Text>
)

const adminReviewQueueHref = (workerShiftAssocId: number) =>
  `${ROUTES.REVIEW}?tab=admin_review&work-id=${workerShiftAssocId}`

export const CompumaticShiftCell = ({ punch }: CompumaticPunchCellsProps) => {
  if (punch.shift === null && punch.workerShiftAssoc === null) {
    return <Text color="lightText">—</Text>
  }

  const shift = punch.shift
  const workerShiftAssoc = punch.workerShiftAssoc

  return (
    <Flex flexDirection="column" gap={1}>
      {shift !== null ? (
        <>
          <Text>{shift.positionName}</Text>
          <Text fontSize={0} color="lightText">
            {format(parseISO(shift.startsAt), 'MMM d h:mm a')} –{' '}
            {format(parseISO(shift.endsAt), 'MMM d h:mm a')}
          </Text>
          <Text fontSize={0}>
            Shift{' '}
            <Link
              to={ROUTES.SHIFT_DETAIL.replace(
                ':shiftId',
                String(shift.shiftId)
              )}
              target="_blank"
            >
              #{shift.shiftId}
            </Link>
          </Text>
        </>
      ) : null}
      {workerShiftAssoc !== null ? (
        <Flex flexDirection="column" gap={0} fontSize={0}>
          <Flex alignItems="center" gap={1} flexWrap="wrap">
            <Text color="lightText">Work {workerShiftAssoc.id}</Text>
            <ShiftStatusRender value={workerShiftAssoc.status} />
          </Flex>
          {workerShiftAssoc.status === 'admin_review' ? (
            <Link to={adminReviewQueueHref(workerShiftAssoc.id)}>
              Open admin review
            </Link>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  )
}
